import { useAtom, useSetAtom } from 'jotai';
import { RESET } from 'jotai/utils';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/lib/v2/components';
import { Flex } from '@/lib/v2/components/Layout/Stack';

import { ContactsViewInfo } from '@/src/ContactsModule/components/ContactsViewInfo/ContactsViewInfo';
import { getDataCardCleaning } from '@/src/ContactsModule/contexts/ImportFileContext/DataCard';
import { IEmblueService } from '@/src/infrastructure/Protocol/IEmblueService';
import { useService } from '@/src/infrastructure/Protocol/useEmblue';
import { ImportSteps } from '@/src/modules/ContactsModule/screens/ImportMain/ImportStepEnum.enum';
import { loadingStatus } from '@/src/presentation/types/enum/importStatus.enum';
import { FileAnalysis } from '@/src/presentation/types/interfaces/IImport.interface';

import {
  atomFileName,
  atomFiles,
  atomIdProcess,
  atomLoadingBar,
  atomShowProgressNotification,
} from '@/modules/ContactsModule/atoms/Steps';

interface IPropsStep3 {
  cancelProcess: () => void;
}

const Step3 = (props: IPropsStep3) => {
  const { cancelProcess } = props;
  const [files, setFiles] = useAtom(atomFiles);
  const { t } = useTranslation();
  const [, setLoadingBar] = useAtom(atomLoadingBar);
  const [loadingBar, setIdProcess] = useAtom(atomIdProcess);
  const [fileName] = useAtom(atomFileName);
  const service = useService() as IEmblueService;
  const setShowProgressNotification = useSetAtom(atomShowProgressNotification);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dataCard = useMemo(() => getDataCardCleaning(t), [t]);

  const setToGroups = useCallback(() => {
    setFiles({ ...files, step: ImportSteps.tagsAndGroup, showStateCard: false });
  }, [files, setFiles]);

  const analyze = useCallback(async () => {
    const importsOptions = files?.importsOptions;
    const fileNameAux = files?.file?.name ?? fileName;

    if (importsOptions && fileNameAux) {
      const {
        mainEmailColumn: emailColumn,
        columnsProcess,
        delimiter: auxDelimiter,
      } = importsOptions;

      const fileAnalysis: FileAnalysis | undefined = await service.analyze({
        file: fileNameAux,
        mainEmailColumn: emailColumn,
        columnsProcess: columnsProcess,
        delimiter: auxDelimiter,
        idEncoding: '0',
        reprocessFile: false,
        hasHead: true,
      });
      if (fileAnalysis) {
        const { idProcess: auxIdProcess } = fileAnalysis;
        setIdProcess(auxIdProcess);
        setShowProgressNotification(RESET);
      }
    }
  }, [
    fileName,
    files?.file?.name,
    files?.importsOptions,
    service,
    setIdProcess,
    setShowProgressNotification,
  ]);

  const analyzeContacts = useCallback(() => {
    setLoadingBar(true);
    setFiles((prevFiles) => ({
      ...prevFiles,
      showStateCard: true,
    }));
    void analyze();
  }, [analyze, setFiles, setLoadingBar]);

  useEffect(() => {
    if (files?.step === ImportSteps.dataCleaning && !files?.showStateCard && !loadingBar) {
      analyzeContacts();
    }
  }, [analyzeContacts, loadingBar, files]);

  if (files.step !== ImportSteps.dataCleaning) return null;

  return (
    <>
      <div className="p-6 pb-10">
        <ContactsViewInfo analysisResult={files?.analysisResult} dataCard={dataCard} />
      </div>
      <div className="w-full border-t border-emblue-borders px-10 py-2">
        <Flex column alignment="end" itemAlignment="end">
          <Flex withGap alignment="end" gapSize="small">
            <Button
              outline
              secondary
              disabled={!files.state || files.state === loadingStatus.loading}
              id="import-cancel"
              onClick={cancelProcess}
            >
              {t('IMPORT_MAIN.cancel')}
            </Button>
            <Button
              disabled={!(files.state === loadingStatus.ready)}
              id="import-continue"
              onClick={setToGroups}
            >
              {t('IMPORT_MAIN.next')}
            </Button>
          </Flex>
        </Flex>
      </div>
    </>
  );
};

export default memo(Step3);
